import { useEffect, useState } from "react";

import classes from "./index.module.scss";

import LineChart from "../../components/lineChart";
import Map from "../../components/map";
import PieChart from "../../components/pieChart";

import { devicesApi, sensorApi, userApi } from "../../api";
import { Device, DeviceStats } from "../../api/types/devices";
import { UserStats } from "../../api/types/user";
import { SensorReadings } from "../../api/interfaces";

const Home = () => {
  const [mapData, setMapData] = useState<Device[]>([]);
  const [deviceStats, setDeviceStats] = useState<DeviceStats | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userDataLoading, setUserDataLoading] = useState<boolean>(false);

  const [pm25, setPm25] = useState<SensorReadings[]>([]);
  const [humidity, setHumidity] = useState<SensorReadings[]>([]);
  const [temp, setTemp] = useState<SensorReadings[]>([]);
  const [co2, setCO2] = useState<SensorReadings[]>([]);

  useEffect(() => {
    const updateDevices = async () => {
      setIsLoading(true);
      devicesApi.getAllDevices({ withLocation: true }).then((data) => {
        if (data.status < 400) {
          const { devices, stats } = data.params;
          setMapData(devices);
          setDeviceStats(stats);
        }
        setIsLoading(false);
      });
    };

    const getSensorReadings = async () => {
      sensorApi
        .getAllUnitsAvgReadings({ period: "week", sensorType: "pm25" })
        .then((data) => {
          if (data.status < 400) {
            const { readings } = data.params;
            setPm25(readings);
          }
        });

      sensorApi
        .getAllUnitsAvgReadings({ period: "week", sensorType: "co2" })
        .then((data) => {
          if (data.status < 400) {
            const { readings } = data.params;
            setCO2(readings);
          }
        });

      sensorApi
        .getAllUnitsAvgReadings({ period: "week", sensorType: "humidity" })
        .then((data) => {
          if (data.status < 400) {
            const { readings } = data.params;
            setHumidity(readings);
          }
        });

      sensorApi
        .getAllUnitsAvgReadings({ period: "week", sensorType: "temp" })
        .then((data) => {
          if (data.status < 400) {
            const { readings } = data.params;
            setTemp(readings);
          }
        });
    };

    const getUsers = async () => {
      setUserDataLoading(true)
      userApi.getAllUsers().then((data) => {
        if (data.status < 400) {
          setUserStats(data.params.stats);
        }
        setUserDataLoading(false)
      });
    };

    getSensorReadings();
    getUsers();
    updateDevices();
  }, []);

  return (
    <div className={classes.root}>
      <div style={{ flexDirection: "row", display: "flex" }}>
        <Map dataPoints={mapData} {...{ isLoading }} />
        <div className={classes.sensorWeeklyAvgChartsContainer}>
          <LineChart
            title="PM2.5 (last 7 days)"
            sensorType="pm2.5"
            containerHeight="35%"
            data={pm25}
            isLoading={false}
            simple
            period="week"
          />
          <LineChart
            title="CO2 (last 7 days)"
            sensorType="co2"
            containerHeight="35%"
            data={co2}
            isLoading={false}
            simple
            period="week"
          />
          <LineChart
            title="Humidity (last 7 days)"
            sensorType="humidity"
            containerHeight="35%"
            data={humidity}
            isLoading={false}
            simple
            period="week"
          />
          <LineChart
            title="Temperature (last 7 days)"
            sensorType="temp"
            containerHeight="35%"
            data={temp}
            isLoading={false}
            simple
            period="week"
          />
        </div>
      </div>
      <div style={{ flexDirection: "row", display: "flex" }}>
        <PieChart
          {...{ isLoading }}
          data={[
            {
              id: "on",
              label: "on",
              value: deviceStats?.on,
              color: "#0ead69",
            },
            {
              id: "off",
              label: "off",
              value: deviceStats?.off,
              color: "#e5383b",
            },
            {
              id: "offline",
              label: "offline",
              value: deviceStats?.offline,
              color: "#FFB703",
            },
          ]}
          total={deviceStats?.total}
          title="Units"
          overlaySubtitle="units"
        />
        <PieChart
          isLoading={userDataLoading}
          data={[
            {
              id: "EU",
              label: "EU",
              value: userStats?.eu,
              color: "#003399",
            },
            ...(userStats?.us && userStats?.us > 0
              ? [
                  {
                    id: "US",
                    label: "US",
                    value: userStats?.us,
                    color: "#b32134qnn",
                  },
                ]
              : []),
          ]}
          total={userStats?.total}
          title="Users"
          overlaySubtitle="users"
        />
      </div>
    </div>
  );
};

export default Home;
